import React from 'react';
import { Link } from 'react-router-dom';

/**
 * The one button in the app. Every call site picks a `variant` + `size` rather
 * than hand-rolling its own padding/colour/shadow combination — that drift is
 * what produced five visually distinct "primary" buttons.
 *
 * Renders a <button> by default, an <a> when `href` is set, and a react-router
 * <Link> when `to` is set, so navigation CTAs share the exact same skin.
 *
 * Every size is at least 44px tall (`min-h-[44px]`) except `size="icon"`, which
 * stays visually compact and gets its 44×44 pointer target from `.tap-target`.
 */

const BASE =
    'inline-flex items-center justify-center gap-2 font-semibold rounded-full ' +
    'transition-colors duration-150 disabled:opacity-60 disabled:pointer-events-none';

const VARIANTS = {
    // Green brand CTA — "Download", "Search", primary form submit.
    primary:
        'bg-brand-500 text-white shadow-e1 hover:bg-brand-600 active:bg-brand-700',
    // Dark/light inverse — nav "Log in", secondary page CTAs.
    neutral:
        'bg-gray-900 text-white hover:bg-gray-700 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200',
    // Outlined — "Load more", tertiary actions.
    outline:
        'border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 ' +
        'hover:border-brand-400 hover:text-brand-600 dark:hover:text-brand-400',
    // Text-only — inline / low-emphasis actions.
    ghost:
        'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white',
    // Translucent, for use over imagery.
    glass:
        'bg-white/20 backdrop-blur border border-white/40 text-white shadow-e2 hover:bg-white/30 hover:border-white/80',
};

const SIZES = {
    sm: 'min-h-[44px] px-4 text-sm',
    md: 'min-h-[44px] px-5 text-sm',
    lg: 'min-h-[44px] px-7 py-3 text-base',
    // Compact square control; `.tap-target` supplies the 44×44 hit area.
    icon: 'h-9 w-9 shrink-0 tap-target',
};

const Button = React.forwardRef(function Button(
    { variant = 'primary', size = 'md', className = '', to, href, children, ...rest },
    ref
) {
    const classes = `${BASE} ${VARIANTS[variant] || VARIANTS.primary} ${SIZES[size] || SIZES.md} ${className}`;

    if (to) {
        return (
            <Link ref={ref} to={to} className={classes} {...rest}>
                {children}
            </Link>
        );
    }
    if (href) {
        return (
            <a ref={ref} href={href} className={classes} {...rest}>
                {children}
            </a>
        );
    }
    return (
        <button ref={ref} type="button" className={classes} {...rest}>
            {children}
        </button>
    );
});

export default Button;
